// react
import { useEffect, useRef, useState } from "react";

import useConfirm from "../useConfirm";

interface BaseProps {
	message?: string;
	initialState?: boolean;
	disabled?: boolean;
	disableReactWarning?: boolean;
	blockSearchParamChanges?: boolean;
}

const useUnsavedChanges = (props?: BaseProps) => {
	const {
		message = "Are you sure you want to leave?",
		initialState = false,
		disabled = false,
		disableReactWarning = false,
		blockSearchParamChanges = true,
	} = props || {};

	const [isUnsafe, setIsUnsafe] = useState(initialState);
	const originalPathnameRef = useRef(window.location.pathname);

	const setIsUnsafeTrue = () => {
		setIsUnsafe(true);
	};

	const setIsUnsafeFalse = () => {
		setIsUnsafe(false);
	};

	const { isActive, onConfirm, onCancel, blocker } = useConfirm({
		when: isUnsafe,
		disabled: disabled || disableReactWarning,
	});

	useEffect(() => {
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (isUnsafe && !disabled) {
				event.preventDefault();
				event.returnValue = message;
			}
		};

		window.addEventListener("beforeunload", handleBeforeUnload);

		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
		};
	}, [disabled, isActive, message, isUnsafe]);

	useEffect(() => {
		if (isActive) {
			const isOnlySearchParamChange =
				!!blocker?.location?.pathname && blocker.location?.pathname === originalPathnameRef.current;
			if (disabled || (!blockSearchParamChanges && isOnlySearchParamChange)) {
				onConfirm();
			} else if (window.confirm(message)) {
				onConfirm();
			} else {
				onCancel();
			}
		}
	}, [isActive, isUnsafe]);

	return {
		setIsSafe: setIsUnsafeFalse,
		setIsUnsafe: setIsUnsafeTrue,
		isUnsafe,
	};
};

export default useUnsavedChanges;
